/**
 * The trainer's spot, drawn on the art kit.
 *
 * PHASE 9 — THE CLASSIC FELT IS RETIRED, AND THIS WAS THE LAST PLACE IT WAS DRAWN.
 * `components/PokerTable.tsx` was the drill's diagram: a CSS oval with a walnut border,
 * the old felt crest and plates of its own (`.felt-*`, `.seat-*` in `app.css`). The
 * trainer is the paid product and a subscriber spends more time on this screen than any
 * other, so it was the last screen still drawn on a table the rest of the product had
 * left. This is the same diagram on the kit: the top-down plate `TopDownTable` sits on,
 * the kit's plate (`.td-tag` shape) for each seat, the kit's pot pill, `PlayingCard`.
 *
 * What it is NOT is `TopDownTable`. That is a scene — clocks, portraits, a flight
 * layer, an action band — driven from a live `TableState`. A spot has none of those: it
 * is six or nine positions, their stacks in big blinds, what each has posted, and who
 * shoved. So the arithmetic is still `seatsFor()` (`lib/table.ts`, tested in
 * `trainer.test.ts`), unchanged, and only the drawing moved.
 *
 * Everything here is derived from the chart node. Nothing is invented for decoration:
 *   - the seats and their stacks come from `stacksBb`
 *   - who shoved comes from the node path — "root/UTG:shove/MP" means UTG jammed and
 *     the hero is MP, so the seats between them folded and the ones after are live
 *   - the blinds come from the table size, the ante from the pot
 */
import type { ChartNode } from "../lib/api";
import { RANKS, type DealtCard } from "../lib/cards";
import { seatsFor } from "../lib/table";
import { PlayingCard } from "../components/PlayingCard";
import "./spot.css";

/** The plate `TopDownTable` draws — one plate for every poker table in the product. */
const PLATE = "/art/table/poker-topdown-1100.webp";

/**
 * `DealtCard.suit` is already 0-3 in the same c/d/h/s order `table-game.ts` uses, so
 * the drill's `{rank, suit}` pair converts to `PlayingCard`'s numeric card without a
 * remap — there is one suit table in the product, and this does not start a second.
 */
const cardIndex = (c: DealtCard): number => {
  const r = RANKS.indexOf(c.rank);
  return r < 0 ? 0 : (r << 2) | c.suit;
};

const bb = (v: number) => (Math.abs(v % 1) < 1e-9 ? v.toFixed(0) : v.toFixed(2));
const pct = (n: number) => `${n.toFixed(3)}%`;

export function SpotTable({ node, cards, handCode }: {
  node: ChartNode; cards: DealtCard[]; handCode: string;
}) {
  const { seats } = seatsFor(node);
  const jammer = seats.find((s) => s.state === "allin");
  const waiting = seats.filter((s) => s.state === "waiting").length;

  /**
   * One sentence carrying everything the picture carries, for anyone who is not
   * looking at it. The table itself is presentational so a screen reader reads this
   * instead of twenty scattered numbers.
   */
  const summary =
    `${seats.length}-handed table. You are in the ${node.actor} with ${bb(seats.find((s) => s.state === "hero")?.stack ?? 0)} big blinds` +
    ` holding ${handCode}. ` +
    (jammer ? `${jammer.name} has moved all in for ${bb(jammer.posted)}. ` : "Folded to you. ") +
    `Pot is ${bb(node.potBb)} big blinds. ${waiting} ${waiting === 1 ? "player" : "players"} still to act behind you.`;

  return (
    <div className="sp-wrap" role="img" aria-label={summary} data-seats={seats.length}>
      <div className="sp-felt" data-td="felt">
        <img className="sp-plate" src={PLATE} alt="" aria-hidden="true" />

        <span className="sp-pot" data-td="pot">
          <span className="sp-pot-label">Pot</span>
          <b className="num">{bb(node.potBb)} bb</b>
        </span>

        {seats.map((s) => (
          <div key={s.name} className={`sp-seat sp-${s.state}`} data-td="seat"
               style={{
                 /* The same bearing on two ovals: the landscape plate, and the plate
                    turned upright for a phone's column (`spot.css`). */
                 "--x": pct(s.x), "--y": pct(s.y),
                 "--xp": pct(50 + 36 * Math.cos(s.angle)), "--yp": pct(50 + 41 * Math.sin(s.angle)),
               } as React.CSSProperties}>
            {s.state === "hero" && (
              <span className="sp-cards" data-td="hand">
                {cards.map((c, i) => <PlayingCard key={i} kind="drill" card={cardIndex(c)} />)}
              </span>
            )}
            <span className="sp-tag">
              <b>{s.name}</b>
              <span className="num sp-stack">{bb(s.stack)}</span>
              {s.state === "allin" && <span className="sp-state sp-state-allin">all in</span>}
              {s.state === "folded" && <span className="sp-state">folded</span>}
            </span>
            {s.posted > 0 && s.state !== "folded" && (
              <span className="sp-post num" data-td="bet">{bb(s.posted)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
